import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Collapse, Descriptions, Input, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { sanitizeNumber } from '@shared/utils/number';
import type { GetDaDeskPortCallInfoResponse } from '@api/features/ops/getDaDeskPortCallInfo';
import type {
	InvoiceOutputBase,
	InvoiceType,
	InvoiceLineType,
	DisDecimal,
	DecimalWithCurrency,
} from '@api/lib/dis/types';
import type { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import { getDaDeskPortCallInfo } from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import { sendRequest } from '@client/lib/sendRequest';
import LoadingIndicator from '@client/components/LoadingIndicator';

type Props = {
	vesselId: number | undefined;
	portCall: ItineraryPortCallDto;
};

type InvoiceTypeKey = 'proforma' | 'apr' | 'actual' | 'dda';

const INVOICE_TYPE_LABELS: Record<InvoiceTypeKey, string> = {
	proforma: 'Proforma',
	apr: 'APR',
	actual: 'Actual',
	dda: 'DDA',
};

const ERROR_KEYS: Record<InvoiceTypeKey, keyof NonNullable<GetDaDeskPortCallInfoResponse['errors']>> = {
	proforma: 'proformaInvoice',
	apr: 'aprInvoice',
	actual: 'actualInvoice',
	dda: 'ddaInvoice',
};

const formatAmount = (val: DisDecimal | undefined, currency?: string): string => {
	if (val == null) {
		return '—';
	}

	const n = sanitizeNumber(val);
	const formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

	if (currency) {
		return `${formatted} ${currency}`;
	}

	return formatted;
};

const extractCostItemName = (line: InvoiceLineType): string => {
	if (line.standardCostItem && typeof line.standardCostItem === 'object') {
		const sci = line.standardCostItem as Record<string, unknown>;
		const name = sci.name ?? sci.label ?? sci.costItemName ?? sci.standardCostItemName;

		if (name) return String(name);
	}

	if (line.customerSpecificCostItemName && typeof line.customerSpecificCostItemName === 'object') {
		const csci = line.customerSpecificCostItemName as Record<string, unknown>;
		const label = csci.name ??
			csci.label ??
			(csci.costItemLabel as Record<string, unknown> | undefined)?.label;

		if (label) return String(label);
	}

	return `Line ${line.lineID ?? ''}`;
};

const getInvoiceLines = (invoice: InvoiceType): InvoiceLineType[] => {
	if (invoice.proformaInvoiceDetail?.invoiceLine) {
		return invoice.proformaInvoiceDetail.invoiceLine;
	}

	const aprDetail = invoice.aprInvoiceDetail ?? invoice.aprinvoiceDetail;

	if (aprDetail?.invoiceLine) {
		return aprDetail.invoiceLine;
	}

	const ddaDetail = invoice.ddaInvoiceDetail ?? invoice.ddainvoiceDetail;

	if (ddaDetail?.invoiceLine) {
		return ddaDetail.invoiceLine;
	}

	if (invoice.actualInvoiceDetail?.invoiceLineWithVoucher) {
		return invoice.actualInvoiceDetail.invoiceLineWithVoucher
			.map((lv) => lv.invoiceLine)
			.filter((l): l is InvoiceLineType => l != null);
	}

	return [];
};

const readInvoiceDaTotalPayload = (inv: InvoiceType): DecimalWithCurrency | undefined => {
	const t = inv.invoiceHeader?.totalAmount;

	return t?.DAtotalAmount ?? t?.dAtotalAmount ?? t?.datotalAmount;
};

type CostRow = {
	key: string;
	costItem: string;
	amount: number;
	currency: string | undefined;
};

const costColumns: ColumnsType<CostRow> = [
	{
		title: 'Cost Item',
		dataIndex: 'costItem',
		key: 'costItem',
	},
	{
		title: 'Amount',
		dataIndex: 'amount',
		key: 'amount',
		align: 'right',
		render: (val: number, row: CostRow) => formatAmount(val, row.currency),
	},
];

const buildCostRows = (output: InvoiceOutputBase): CostRow[] => {
	const invoices = output.invoices?.invoice ?? [];
	let rowIndex = 0;

	return invoices.flatMap((inv) => {
		const lines = getInvoiceLines(inv);
		const currency = inv.invoiceHeader?.daCurrency ?? '';

		return lines.map((line) => {
			const amountObj = line.amount as Record<string, unknown> | undefined;
			const daAmount = (amountObj?.DAamount ?? amountObj?.daamount ?? amountObj?.DAAmount) as
				{ value?: DisDecimal; currency?: string; dacurrency?: string; DACurrency?: string } | undefined;
			const keyFallback = rowIndex;
			rowIndex += 1;

			return {
				key: `${inv.invoiceHeader?.invoiceID ?? ''}-${line.lineID ?? keyFallback}`,
				costItem: extractCostItemName(line),
				amount: sanitizeNumber(daAmount?.value),
				currency: daAmount?.currency ?? daAmount?.dacurrency ?? daAmount?.DACurrency ?? currency,
			};
		});
	});
};

const getInvoiceTotal = (output: InvoiceOutputBase): { value: number; currency: string } | null => {
	const invoices = output.invoices?.invoice ?? [];

	const inv = invoices.find((candidate) => readInvoiceDaTotalPayload(candidate) != null);

	if (inv == null) {
		return null;
	}

	const total = readInvoiceDaTotalPayload(inv);

	if (total == null) {
		return null;
	}

	return {
		value: sanitizeNumber(total.value),
		currency: total.currency ?? total.dacurrency ?? inv.invoiceHeader?.daCurrency ?? '',
	};
};

const InvoiceSection: React.FC<{
	label: string;
	output: InvoiceOutputBase;
}> = ({ label, output }) => {
	const rows = useMemo(() => buildCostRows(output), [output]);
	const total = useMemo(() => getInvoiceTotal(output), [output]);
	const invoiceNumber = output.invoices?.invoice?.[0]?.invoiceHeader?.invoiceNumber;
	const title = invoiceNumber ? `${label} — #${invoiceNumber}` : label;

	return (
		<div>
			<Typography.Text strong>{title}</Typography.Text>
			<Table<CostRow>
				dataSource={rows}
				columns={costColumns}
				pagination={false}
				size="small"
				style={{ marginTop: 8 }}
				summary={() => (
					total != null ? (
						<Table.Summary.Row>
							<Table.Summary.Cell index={0}>
								<Typography.Text strong>Total</Typography.Text>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={1} align="right">
								<Typography.Text strong>
									{formatAmount(total.value, total.currency)}
								</Typography.Text>
							</Table.Summary.Cell>
						</Table.Summary.Row>
					) : null
				)}
			/>
		</div>
	);
};

const DaDeskTab = ({ vesselId, portCall }: Props) => {
	const { isAdmin } = useAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<GetDaDeskPortCallInfoResponse | null>(null);

	const hasLink = useMemo(() => (
		portCall.daDeskExternalId != null || portCall.daDeskInterfaceUniqueReference != null
	), [portCall.daDeskExternalId, portCall.daDeskInterfaceUniqueReference]);

	const fetchInfo = async () => {
		if (vesselId == null) {
			setError('Missing vessel id');

			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await getDaDeskPortCallInfo(vesselId, portCall.id);
			setData(res);
		} catch (e) {
			setError((e as Error)?.message ?? 'Failed to load DA Desk info');
		} finally {
			setLoading(false);
		}
	};

	const [simOpen, setSimOpen] = useState(false);
	const [simLoading, setSimLoading] = useState(false);
	const [simResult, setSimResult] = useState<Record<string, unknown> | null>(null);
	const [simDaType, setSimDaType] = useState<string>('FDA');

	const buildSimPayload = useCallback((daType: string) => JSON.stringify({
		principleId: 202019,
		interfaceUniqueReference: portCall.daDeskInterfaceUniqueReference ?? '',
		daApprovalNotification: {
			actor: { actorID: '', actionDate: new Date().toISOString() },
			daType,
			daEvent: 'APPROVED',
			invoiceId: '',
			daReference: '',
		},
	}, null, 2), [portCall.daDeskInterfaceUniqueReference]);

	const [simPayload, setSimPayload] = useState(() => buildSimPayload('FDA'));

	const handleDaTypeChange = (value: string) => {
		setSimDaType(value);
		setSimPayload(buildSimPayload(value));
	};

	const simulateNotification = async () => {
		setSimLoading(true);
		setSimResult(null);

		try {
			const body = JSON.parse(simPayload);
			const res = await sendRequest<object, Record<string, unknown>>(
				'/ops/dis-notifications/simulate',
				'post',
				body,
			);
			setSimResult(res);
			fetchInfo();
		} catch (e) {
			setSimResult({ error: (e as Error)?.message ?? String(e) });
		} finally {
			setSimLoading(false);
		}
	};

	useEffect(() => {
		if (!hasLink) {
			setData(null);
			setError(null);

			return;
		}

		fetchInfo();
		 
	}, [hasLink, vesselId, portCall.id]);

	useEffect(() => {
		if (!isAdmin) {
			setSimOpen(false);
		}
	}, [isAdmin]);

	const invoiceOutputs = data?.invoiceOutputs;

	const grandTotal = useMemo(() => {
		if (invoiceOutputs == null) {
			return null;
		}

		let sum = 0;
		let currency = '';

		(Object.keys(invoiceOutputs) as InvoiceTypeKey[]).forEach((key) => {
			const output = invoiceOutputs[key];

			if (output == null) {
				return;
			}

			const total = getInvoiceTotal(output);

			if (total != null) {
				sum += total.value;

				if (!currency) currency = total.currency;
			}
		});

		return sum !== 0 || currency ? { value: sum, currency } : null;
	}, [invoiceOutputs]);

	if (!hasLink) {
		return (
			<Card>
				<Alert
					type="info"
					showIcon
					message="No DA Desk link"
					description="This port call is not linked to a DA Desk/DIS port call."
				/>
			</Card>
		);
	}

	const invoiceTypeKeys: InvoiceTypeKey[] = ['proforma', 'apr', 'actual', 'dda'];
	const availableInvoices = invoiceTypeKeys.filter(
		(key) => invoiceOutputs?.[key] != null,
	);

	return (
		<Card>
			<Space direction="vertical" style={{ width: '100%' }} size="middle">
				<Space style={{ width: '100%', justifyContent: 'space-between' }}>
					<Typography.Title level={5} style={{ margin: 0 }}>DA Desk</Typography.Title>
					<Space>
						<Button onClick={fetchInfo} disabled={loading}>Refresh</Button>
						{isAdmin && (
							<Button
								type="dashed"
								onClick={() => setSimOpen((v) => !v)}
							>
								{simOpen ? 'Hide' : 'Simulate Notification'}
							</Button>
						)}
					</Space>
				</Space>

				{isAdmin && simOpen && (
					<Card size="small" title="Simulate DIS Notification" style={{ background: '#fffbe6' }}>
						<Space direction="vertical" style={{ width: '100%' }} size="small">
							<Space>
								<Typography.Text>DA Type:</Typography.Text>
								<Select
									value={simDaType}
									onChange={handleDaTypeChange}
									style={{ width: 140 }}
									options={[
										{ value: 'FDA', label: 'FDA (Final)' },
										{ value: 'VI', label: 'VI (Vendor Invoice)' },
										{ value: 'PVI', label: 'PVI (Proforma VI)' },
										{ value: 'PDA', label: 'PDA (Proforma DA)' },
										{ value: 'SDA', label: 'SDA (Supplementary)' },
										{ value: 'APR', label: 'APR' },
										{ value: 'ADR', label: 'ADR' },
									]}
								/>
							</Space>
							<Input.TextArea
								rows={14}
								value={simPayload}
								onChange={(e) => setSimPayload(e.target.value)}
								style={{ fontFamily: 'monospace', fontSize: 12 }}
							/>
							<Button
								type="primary"
								onClick={simulateNotification}
								loading={simLoading}
							>
								Send
							</Button>
							{simResult != null && (
								<Typography.Text code style={{ whiteSpace: 'pre-wrap' }}>
									{JSON.stringify(simResult, null, 2)}
								</Typography.Text>
							)}
						</Space>
					</Card>
				)}

				<Descriptions bordered size="small" column={1}>
					<Descriptions.Item label="DA Desk PortCall Ref">
						{portCall.daDeskExternalId ?? '—'}
					</Descriptions.Item>
					<Descriptions.Item label="Interface Unique Reference">
						{portCall.daDeskInterfaceUniqueReference ?? '—'}
					</Descriptions.Item>
				</Descriptions>

				{loading && <LoadingIndicator />}

				{error != null && (
					<Alert type="error" showIcon message="Failed to load DA Desk info" description={error} />
				)}

				{data?.errors != null && (
					<Alert
						type="warning"
						showIcon
						message="Some DIS calls failed"
						description={(
							<div>
								{data.errors.portcallStatusInfo != null && (
									<div>
										<strong>get-portcall-status-info</strong>: {data.errors.portcallStatusInfo.message}
									</div>
								)}
								{invoiceTypeKeys.map((key) => {
									const errKey = ERROR_KEYS[key];
									const err = data.errors?.[errKey];

									if (err == null) {
										return null;
									}

									return (
										<div key={key}>
											<strong>{INVOICE_TYPE_LABELS[key]} invoice</strong>: {err.message}
										</div>
									);
								})}
							</div>
						)}
					/>
				)}

				{data?.disbursementAccountSync != null && (
					<Alert
						type={data.disbursementAccountSync.status === 'skipped_posted' ? 'warning' : 'success'}
						showIcon
						message={{
							created: 'Port DA created from DA Desk',
							updated: 'Port DA updated from DA Desk',
							skipped_posted: 'Port DA sync skipped — DA is already posted',
							skipped_no_lines: 'No cost lines to sync',
							skipped_fda_exists: 'PDA sync skipped — FDA already exists',
						}[data.disbursementAccountSync.status]}
					/>
				)}

				{grandTotal != null && (
					<Card size="small">
						<Descriptions bordered size="small" column={2}>
							{availableInvoices.map((key) => {
								const output = invoiceOutputs![key]!;
								const total = getInvoiceTotal(output);

								return (
									<Descriptions.Item
										key={key}
										label={`${INVOICE_TYPE_LABELS[key]} Total`}
									>
										{total != null ? formatAmount(total.value, total.currency) : '—'}
									</Descriptions.Item>
								);
							})}
							<Descriptions.Item label="Grand Total">
								<Typography.Text strong>
									{formatAmount(grandTotal.value, grandTotal.currency)}
								</Typography.Text>
							</Descriptions.Item>
						</Descriptions>
					</Card>
				)}

				{availableInvoices.length > 0 && (
					<Collapse
						defaultActiveKey={availableInvoices}
						items={availableInvoices.map((key) => ({
							key,
							label: `${INVOICE_TYPE_LABELS[key]} Costs`,
							children: (
								<InvoiceSection
									label={INVOICE_TYPE_LABELS[key]}
									output={invoiceOutputs![key]!}
								/>
							),
						}))}
					/>
				)}

				{data?.portcallStatusInfo != null && (
					<Card size="small" title="Portcall status info">
						<Typography.Paragraph style={{ marginBottom: 0 }}>
							<Typography.Text code style={{ whiteSpace: 'pre-wrap' }}>
								{JSON.stringify(data.portcallStatusInfo, null, 2)}
							</Typography.Text>
						</Typography.Paragraph>
					</Card>
				)}
			</Space>
		</Card>
	);
};

export default DaDeskTab;
